import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { getCurrentUserId, getCurrentUserRole } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { API_ENDPOINTS } from '../../config/env';
import { asArray } from '../../utils/apiResponse';

/** Payout line — same shape as Settlements (`/Transactions/payouts`). */
interface PayoutRecord {
  id: number;
  transactionId: number;
  type: 'Admin' | 'Agent' | 'SuperAgent' | 'Company' | string;
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface TransactionFees {
  superAgentPercent: number;
  agentPercent: number;
  superAgent: number;
  agent: number;
  netPayout: number;
}

/** Transaction — same fields as TransactionList (`/Transactions`). */
interface ApiTransaction {
  id: number;
  userId: number;
  provider: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoAmount: number;
  userWallet: string;
  uniqueWalletAddress: string;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed';
  timestamp: string;
  fees: TransactionFees;
}

type ReconciliationQueueRow = {
  key: string;
  payoutId: number;
  transactionId: number;
  transactionAt: string;
  primaryStatus: string;
  secondaryBadge: string;
  received: string;
  distributed: string;
  variance: string;
  commercialAmount: string;
  paymentSource: string;
  payoutAddress: string;
  chainRecord: string;
  txHash: string | null;
};

type AppliedReconFilters = {
  accountId: string;
  dateFrom: string;
  dateUntil: string;
};

const tabs = ['All Groups', 'Need Reviews', 'Matched', 'Missing Data'];

/** Match Settlements default `itemsPerPage` — larger values have caused payout API errors on some backends. */
const PAYOUT_PAGE_SIZE = 25;
/** Slightly larger list for joining payout lines to orders (TransactionList often uses 10 per page). */
const TRANSACTION_PAGE_SIZE = 50;

/** If only one date is set, mirror to the same calendar day so the API always gets a pair or neither (avoids server 500s). */
function normalizeDateRangeBounds(dateFrom: string, dateUntil: string): { from: string; until: string } | null {
  let from = dateFrom.trim();
  let until = dateUntil.trim();
  if (from && !until) until = from;
  if (until && !from) from = until;
  if (!from && !until) return null;
  return { from, until };
}

/**
 * Payouts endpoint expects the same `dateFrom` / `dateTo` shape as Settlements (datetime-local style, no ms suffix).
 */
function payoutQueryDates(dateFrom: string, dateUntil: string): { dateFrom: string; dateTo: string } | null {
  const b = normalizeDateRangeBounds(dateFrom, dateUntil);
  if (!b) return null;
  return {
    dateFrom: `${b.from}T00:00`,
    dateTo: `${b.until}T23:59`,
  };
}

function transactionQueryIsoRange(dateFrom: string, dateUntil: string): { dateFrom: string; dateTo: string } | null {
  const b = normalizeDateRangeBounds(dateFrom, dateUntil);
  if (!b) return null;
  return {
    dateFrom: new Date(`${b.from}T00:00:00`).toISOString(),
    dateTo: new Date(`${b.until}T23:59:59.999`).toISOString(),
  };
}

async function readHttpErrorDetail(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as Record<string, unknown>;
    if (typeof j.message === 'string' && j.message) return j.message;
    if (typeof j.title === 'string' && j.title) return j.title;
    if (typeof j.error === 'string' && j.error) return j.error;
    const ex = j.expandedError;
    if (ex && typeof ex === 'object' && ex !== null && 'message' in ex) {
      const m = (ex as { message: unknown }).message;
      if (typeof m === 'string' && m) return m;
    }
  } catch {
    /* not JSON */
  }
  const trimmed = text.trim();
  return trimmed ? trimmed.slice(0, 280) : '';
}

const isPayoutAccount = (user: User) => user.role === 'Agent' || user.role === 'SuperAgent';

const truncateAddress = (address: string): string => {
  if (!address || address.length <= 14) return address || '—';
  return `${address.slice(0, 8)}…${address.slice(-6)}`;
};

const parseAmount = (s: string): number => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const formatFiat = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);

const payoutTypeLabel = (t: PayoutRecord['type']) => {
  if (t === 'Admin' || t === 'Company') return 'Com';
  if (t === 'Agent') return 'Dest';
  if (t === 'SuperAgent') return 'SA';
  return String(t);
};

/**
 * One row per transaction: received crypto vs sum of Com + Dest payout lines.
 * Avoids false VARIANCE when comparing full amount to a single payout leg.
 */
function buildQueueRows(settlements: PayoutRecord[], txById: Map<number, ApiTransaction>): ReconciliationQueueRow[] {
  const byTx = new Map<number, PayoutRecord[]>();
  for (const p of settlements) {
    const list = byTx.get(p.transactionId) ?? [];
    list.push(p);
    byTx.set(p.transactionId, list);
  }

  return Array.from(byTx.entries()).map(([txId, payouts]) => {
    const tx = txById.get(txId);
    const unit = (tx?.cryptoCurrency || 'USDC').replace(/_POLYGON$/i, '');
    const totalDist = payouts.reduce((sum, p) => sum + parseAmount(p.amount), 0);
    const crypto = tx?.cryptoAmount ?? NaN;
    const varianceNum = Number.isFinite(crypto) ? crypto - totalDist : NaN;
    const varianceStr = Number.isFinite(varianceNum)
      ? `${varianceNum >= 0 ? '' : '-'}${Math.abs(varianceNum).toFixed(4)} ${unit}`
      : '—';

    const anyFailed = payouts.some((p) => p.status === 'Failed');
    const anyPending = payouts.some((p) => p.status === 'Pending');
    const allCompleted = payouts.every((p) => p.status === 'Completed');

    let primaryStatus = 'REVIEW';
    if (!tx) primaryStatus = 'MISSING TX';
    else if (anyFailed) primaryStatus = 'PAYOUT FAILED';
    else if (anyPending) primaryStatus = 'PENDING PAYOUT';
    else if (allCompleted && Number.isFinite(varianceNum) && Math.abs(varianceNum) < 0.0001) {
      primaryStatus = 'MATCHED';
    } else if (allCompleted) primaryStatus = 'VARIANCE';

    const secondaryBadge = tx
      ? `Order: ${tx.status} · Escrow ${truncateAddress(tx.uniqueWalletAddress || '')}`
      : 'No order';

    const primaryPayout = payouts[0];
    const chainHash = payouts.map((p) => p.txHash?.trim()).find(Boolean) || null;
    const chainRecord = chainHash
      ? `TX ${truncateAddress(chainHash)}`
      : 'No chain hash yet';

    const breakdown = payouts
      .map((p) => `${payoutTypeLabel(p.type)} ${parseAmount(p.amount).toFixed(4)}`)
      .join(' + ');

    return {
      key: `tx${txId}`,
      payoutId: primaryPayout.id,
      transactionId: txId,
      transactionAt: tx
        ? new Date(tx.timestamp).toLocaleString()
        : new Date(primaryPayout.createtAt).toLocaleString(),
      primaryStatus,
      secondaryBadge,
      received: tx ? `${tx.cryptoAmount} ${unit}` : '—',
      distributed: `${totalDist.toFixed(4)} ${unit} (${breakdown})`,
      variance: varianceStr,
      commercialAmount: tx ? formatFiat(tx.fiatAmount, tx.fiatCurrency) : '—',
      paymentSource: tx?.provider || '—',
      payoutAddress: truncateAddress(tx?.uniqueWalletAddress || primaryPayout.toWallet),
      chainRecord,
      txHash: chainHash,
    };
  });
}

const Reconciliation: React.FC = () => {
  const navigate = useNavigate();
  const userRole = getCurrentUserRole() as 'Admin' | 'SuperAgent' | 'Agent' | null;
  const currentUserId = getCurrentUserId();
  const [users, setUsers] = useState<User[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateUntil, setDateUntil] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedReconFilters>({
    accountId: '',
    dateFrom: '',
    dateUntil: '',
  });

  const [settlements, setSettlements] = useState<PayoutRecord[]>([]);
  const [transactionsById, setTransactionsById] = useState<Map<number, ApiTransaction>>(new Map());
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchUsers = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const data = await dashboardService.getUsers();
      setUsers(data);
    } catch (e) {
      console.error('Error loading accounts for reconciliation:', e);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      fetchUsers();
    }
  }, [userRole, fetchUsers]);

  const effectiveUserIdForApi = useCallback((): string => {
    if (userRole === 'Agent') return currentUserId || '';
    return appliedFilters.accountId;
  }, [userRole, currentUserId, appliedFilters.accountId]);

  const fetchQueue = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setQueueError('No authentication token found');
      return;
    }

    setQueueLoading(true);
    setQueueError(null);

    try {
      const uid = effectiveUserIdForApi();

      const payoutParams = new URLSearchParams();
      if (uid) payoutParams.append('userid', uid);
      const payoutDates = payoutQueryDates(appliedFilters.dateFrom, appliedFilters.dateUntil);
      if (payoutDates) {
        payoutParams.append('dateFrom', payoutDates.dateFrom);
        payoutParams.append('dateTo', payoutDates.dateTo);
      }
      payoutParams.append('page', '1');
      payoutParams.append('pageSize', String(PAYOUT_PAGE_SIZE));

      const txParams = new URLSearchParams();
      txParams.append('page', '1');
      txParams.append('pageSize', String(TRANSACTION_PAGE_SIZE));
      if (uid) txParams.append('userId', uid);
      const txDates = transactionQueryIsoRange(appliedFilters.dateFrom, appliedFilters.dateUntil);
      if (txDates) {
        txParams.append('dateFrom', txDates.dateFrom);
        txParams.append('dateTo', txDates.dateTo);
      }

      const [payoutRes, txRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.TRANSACTIONS}/payouts?${payoutParams.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_ENDPOINTS.TRANSACTIONS}?${txParams.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (!payoutRes.ok) {
        const detail = await readHttpErrorDetail(payoutRes.clone());
        throw new Error(
          detail ? `Payouts failed (${payoutRes.status}): ${detail}` : `Payouts request failed (${payoutRes.status})`
        );
      }
      if (!txRes.ok) {
        const detail = await readHttpErrorDetail(txRes.clone());
        throw new Error(
          detail
            ? `Transactions failed (${txRes.status}): ${detail}`
            : `Transactions request failed (${txRes.status})`
        );
      }

      const payoutData = await payoutRes.json();
      const txData = await txRes.json();

      const payoutList = asArray<PayoutRecord>(payoutData);
      const txList = asArray<ApiTransaction>(txData);

      const map = new Map<number, ApiTransaction>();
      for (const t of txList) {
        map.set(t.id, t);
      }

      setSettlements(payoutList);
      setTransactionsById(map);
    } catch (e) {
      console.error('Reconciliation queue fetch error:', e);
      setQueueError(e instanceof Error ? e.message : 'Failed to load reconciliation data');
      setSettlements([]);
      setTransactionsById(new Map());
    } finally {
      setQueueLoading(false);
    }
  }, [appliedFilters, effectiveUserIdForApi]);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  const queueRows = useMemo(
    () => buildQueueRows(settlements, transactionsById),
    [settlements, transactionsById]
  );

  const filteredQueueRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = queueRows;
    if (q) {
      rows = rows.filter(
        (r) =>
          String(r.transactionId).includes(q) ||
          String(r.payoutId).includes(q) ||
          (r.txHash || '').toLowerCase().includes(q) ||
          r.payoutAddress.toLowerCase().includes(q) ||
          r.primaryStatus.toLowerCase().includes(q) ||
          r.received.toLowerCase().includes(q) ||
          r.distributed.toLowerCase().includes(q)
      );
    }
    switch (activeTab) {
      case 1: // Need Reviews
        return rows.filter((r) =>
          ['PAYOUT FAILED', 'PENDING PAYOUT', 'VARIANCE', 'MISSING TX', 'REVIEW'].includes(r.primaryStatus)
        );
      case 2: // Matched
        return rows.filter((r) => r.primaryStatus === 'MATCHED');
      case 3: // Missing Data
        return rows.filter((r) => r.primaryStatus === 'MISSING TX');
      default:
        return rows;
    }
  }, [queueRows, searchQuery, activeTab]);

  const accountOptions = useMemo((): User[] => {
    const eligible = users.filter(isPayoutAccount);
    if (userRole === 'Admin') {
      return eligible;
    }
    if (userRole === 'SuperAgent') {
      const selfId = parseInt(currentUserId || '0', 10);
      return eligible.filter((u) => u.id === selfId || (u.role === 'Agent' && u.id !== selfId));
    }
    if (userRole === 'Agent' && currentUserId) {
      const selfId = parseInt(currentUserId, 10);
      return eligible.filter((u) => u.id === selfId);
    }
    return [];
  }, [users, userRole, currentUserId]);

  const appliedAccountLabel = useMemo(() => {
    const id = appliedFilters.accountId;
    if (!id) return '';
    const u = users.find((x) => x.id === parseInt(id, 10));
    return u ? `${u.name} (${u.role})` : `Account ${id}`;
  }, [appliedFilters.accountId, users]);

  const needsReviewCount = useMemo(
    () =>
      queueRows.filter(
        (r) =>
          r.primaryStatus === 'PAYOUT FAILED' ||
          r.primaryStatus === 'PENDING PAYOUT' ||
          r.primaryStatus === 'VARIANCE' ||
          r.primaryStatus === 'MISSING TX'
      ).length,
    [queueRows]
  );

  const missingDataCount = useMemo(
    () => queueRows.filter((r) => r.primaryStatus === 'MISSING TX').length,
    [queueRows]
  );

  const handleApplyFilters = () => {
    let from = dateFrom;
    let until = dateUntil;
    if (from && until && from > until) {
      from = dateUntil;
      until = dateFrom;
      setDateFrom(from);
      setDateUntil(until);
    }
    setAppliedFilters({
      accountId: userRole === 'Agent' ? '' : selectedAccountId,
      dateFrom: from,
      dateUntil: until,
    });
  };

  const handleRefresh = () => {
    void fetchQueue();
  };

  const exportCsv = () => {
    const header = [
      'payoutId',
      'transactionId',
      'transactionAt',
      'primaryStatus',
      'secondaryBadge',
      'received',
      'distributed',
      'variance',
      'commercialAmount',
      'paymentSource',
      'payoutAddress',
      'txHash',
    ];
    const lines = [
      header.join(','),
      ...filteredQueueRows.map((r) =>
        [
          r.payoutId,
          r.transactionId,
          `"${r.transactionAt}"`,
          r.primaryStatus,
          r.secondaryBadge,
          r.received,
          r.distributed,
          r.variance,
          r.commercialAmount,
          r.paymentSource,
          `"${r.payoutAddress}"`,
          r.txHash || '',
        ].join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dateInputClass =
    'w-full rounded-xl border border-[#d2d9de] bg-[#F0F0F0] p-[12px] text-sm text-slate-800 [color-scheme:light] focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/20';

  const scopeDisplay =
    userRole === 'Agent'
      ? 'Your workspace (agent)'
      : appliedAccountLabel || 'All merchants & agents';

  return (
    <div className="min-h-screen homeBg px-3 py-3 sm:px-4 sm:py-4">
      <div className="mx-auto w-full max-w-[1440px] space-y-[40px] pt-3">

        {/* Header card */}
        <section className="rounded-[30px] border border-[#d7dde1] bg-[#f6f6f6] blurLayerInteractive p-[30px] shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="mb-1 text-base uppercase text-[#4A4A4A]">Invoice Review</p>
              <h1 className="mb-2 text-3xl font-semibold text-[#111827]">Reconciliation</h1>
              <p className="mb-0 max-w-3xl text-sm text-slate-500">
                Compare received USDC against the Com + Dest split for each payment, so your team can
                focus on mismatches instead of checking every settlement line by hand.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={queueLoading}
                className="border-[1px] border-[#d2d8dd] px-[13px] py-2 rounded-[6px] text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {queueLoading ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                disabled={filteredQueueRows.length === 0}
                className="bg-[#2297D1] px-[13px] py-2 rounded-[6px] text-sm font-semibold text-white hover:bg-[#1d84b8] disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#d7dde1] pt-4 md:grid-cols-4">
            <div className="rounded-xl bg-[#F0F0F0] p-3">
              <p className="mb-1 text-[10px] sm:text-base uppercase  text-slate-500">Rows in view</p>
              <p className="mb-0 text-lg font-semibold text-[#111827]">{filteredQueueRows.length}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] p-3">
              <p className="mb-1 text-[10px] sm:text-base uppercase  text-slate-500">Needs review</p>
              <p className="mb-0 text-lg font-semibold text-[#111827]">{needsReviewCount}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] p-3">
              <p className="mb-1 text-[10px] sm:text-base uppercase  text-slate-500">Missing data</p>
              <p className="mb-0 text-lg font-semibold text-[#111827]">{missingDataCount}</p>
            </div>
            <div className="rounded-xl bg-[#F0F0F0] p-3">
              <p className="mb-1 text-[10px] sm:text-base uppercase  text-slate-500">Scope</p>
              <p className="mb-0 text-lg font-semibold text-[#111827]">{scopeDisplay}</p>
            </div>
          </div>
        </section>

        {/* Filter controls card */}
        <section className="rounded-[30px] border border-[#d7dde1] bg-[#f6f6f6]  shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
         <div className='p-[30px] border-b border-[#d7dde1]'>
           <h2 className="mb-1 text-2xl font-semibold text-[#111827]">Review controls</h2>
          <p className="  text-sm text-slate-500">
            Narrow the queue by review state, payout status, account, date window, or payment reference
            without leaving this page.
          </p>
         </div>

          <div className='p-[30px]'>
            <div className="mb-4 flex flex-wrap gap-2">
            {tabs.map((tab, index) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(index)}
                className={`border-[1px] px-[13px] py-2 rounded-xl text-sm font-semibold ${
                  index === activeTab
                    ? 'border-[#2297D1] bg-[#2297D1] text-white'
                    : 'border-[#d2d9de] bg-white text-[#111827] hover:bg-slate-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5 ">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Status</p>
              <input
                className="w-full rounded-xl border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-600"
                placeholder="Use tabs above"
                readOnly
                value={tabs[activeTab] || ''}
              />
            </div>
            <div>
              <label htmlFor="recon-account" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Account
              </label>
              {userRole === 'Admin' || userRole === 'SuperAgent' ? (
                <select
                  id="recon-account"
                  className="w-full rounded-xl border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-800"
                  value={selectedAccountId}
                  disabled={accountsLoading}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  <option value="">
                    {accountsLoading ? 'Loading accounts…' : 'All merchants & agents'}
                  </option>
                  {accountOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.role === 'SuperAgent' ? 'Super agent' : 'Agent'} (ID {u.id})
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="recon-account"
                  className="w-full rounded-xl border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-600"
                  value={currentUserId || ''}
                  disabled
                >
                  <option value={currentUserId || ''}>
                    {userRole === 'Agent' ? 'Your agent account' : 'Account'} (scoped by login)
                  </option>
                </select>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Search</p>
              <div className="flex gap-1">
              <input
                className="w-full rounded-xl border border-[#66666685] bg-[#F0F0F0] p-[12px] text-sm text-slate-800"
                placeholder="Tx id, escrow, hash…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                onClick={handleApplyFilters}
                className="  px-[13px] py-2.5 rounded-4 text-base font-semibold border hover:bg-[#1b7fb3]"
              >
                Apply
              </button>
            </div>
            </div>
            <div>
              <label htmlFor="recon-date-from" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                From
              </label>
              <input
                id="recon-date-from"
                type="date"
                className={dateInputClass}
                value={dateFrom}
                max={dateUntil || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="recon-date-until" className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Until
              </label>
              <input
                id="recon-date-until"
                type="date"
                className={dateInputClass}
                value={dateUntil}
                min={dateFrom || undefined}
                onChange={(e) => setDateUntil(e.target.value)}
              />
            </div>
            
          </div>
          <div className='xl:w-[300px] mt-4'>
            <p className='text-base font-bold'>Current review view</p>
            <p>All payout rows. Showing every row visible to Merchant. Export from the header when finance needs the same view in CSV form.</p>
          </div>
          </div>
        </section>

        {/* Review queue card */}
        <section className="rounded-[30px] border border-[#d7dde1] bg-[#f6f6f6] overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/90   py-[18px] px-[30px]">
            <div>
              <h2 className="text-2xl font-semibold text-[#111827]">Review queue</h2>
              <p className="mt-2 text-sm text-slate-500">
                Open the transaction only when you need the full case history. Routine payout checks can stay right here.
              </p>
            </div>
            <p className="text-sm font-medium text-slate-600">{filteredQueueRows.length} total rows</p>
          </div>

          <div className="p-[30px]">
            {queueError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {queueError}
              </div>
            )}
            {queueLoading && filteredQueueRows.length === 0 && (
              <div className="flex justify-center py-16">
                <Spinner animation="border" role="status" style={{ color: '#2297D1' }}>
                  <span className="visually-hidden">Loading…</span>
                </Spinner>
              </div>
            )}
            {queueLoading && filteredQueueRows.length > 0 && (
              <div className="flex justify-center py-2">
                <Spinner animation="border" size="sm" role="status" style={{ color: '#2297D1' }}>
                  <span className="visually-hidden">Refreshing…</span>
                </Spinner>
              </div>
            )}

            <div className="space-y-5">
              {filteredQueueRows.map((row) => (
                <article
                  key={row.key}
                  className="rounded-4 border border-[#d1d9e0] p-4 shadow-lg"
                  style={{   borderLeft: '6px solid #2297D1' }}
                >
                 <div className='border-l-4 border-[#2297D1] pl-4'>
                   <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-[0.12em] text-slate-500">
                        Transaction / payout
                      </p>
                      <p className="mb-0 text-base font-semibold text-[#111827]">
                        #{row.transactionId} · payout #{row.payoutId}
                      </p>
                      <p className="mb-0 mt-0.5 text-xs text-slate-500">Transaction: {row.transactionAt}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-[#2297D121] px-3 py-2 text-[10px] sm:text-sm font-semibold tracking-[0.12em] text-[#0F7EB4]">
                        {row.primaryStatus}
                      </span>
                      <span className="rounded-full bg-[#EBEBEB] px-3 py-2 text-[10px] sm:text-sm font-semibold tracking-[0.12em] text-[#4B5563]">
                        {row.secondaryBadge}
                      </span>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <InfoCell label="Received (order)" value={row.received} />
                    <InfoCell label="Distributed (payout)" value={row.distributed} />
                    <InfoCell label="Variance" value={row.variance} />
                    <InfoCell label="Commercial amount" value={row.commercialAmount} />
                    <InfoCell label="Payment source" value={row.paymentSource} />
                    <InfoCell label="Payout address" value={row.payoutAddress} />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    {row.txHash ? (
                      <a
                        href={`https://polygonscan.com/tx/${row.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold tracking-[0.12em] text-[#0F7EB4] hover:underline"
                      >
                        {row.chainRecord} — open on Polygonscan
                      </a>
                    ) : (
                      <p className="text-xs font-semibold tracking-[0.12em] text-slate-500">
                        {row.chainRecord}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        navigate('/transactions', { state: { openTransactionId: row.transactionId } })
                      }
                      className="border-[1px] border-[#d2d8dd] bg-white px-[13px] py-2 rounded-2xl text-sm font-semibold text-[#111827] hover:bg-slate-50"
                    >
                      Open Transaction
                    </button>
                  </div>
                 </div>
                </article>
              ))}
            </div>

            {!queueLoading && filteredQueueRows.length === 0 && !queueError && (
              <p className="py-10 text-center text-sm text-slate-500">No payout lines in this view.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const InfoCell: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div className="rounded-xl border border-[#eef2f6] bg-white px-3 py-3">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mb-0 text-sm font-semibold text-[#111827]">{value}</p>
    </div>
  );
};

export default Reconciliation;
